/**
 * PaywallDiagnosticsPanel — production-safe IAP / paywall activation surface
 * (push9 / v1.4.3-b9).
 *
 * Renders a one-shot snapshot of the RevenueCat / StoreKit wiring so a
 * TestFlight tester or operator can confirm exactly why purchases are (or are
 * not) available — instead of the old silent `console.warn("[iap] ... IAP
 * disabled")` that no trainer ever saw.
 *
 * It reports, without ever exposing the API key value:
 *   - RevenueCat public key loaded true/false (+ which env var supplied it)
 *   - native plugin available, SDK configured
 *   - current offering loaded, package count + product ids
 *   - server-mirrored entitlement / subscription tier
 *   - last restore result + error, last purchase error, last configure error
 *   - whether IAP is disabled and the precise reason
 *
 * Mirrors BroadcastDiagnosticsPanel: read-only, JSON-only (no secrets), gated
 * behind the parent Settings diagnostics collapsible so a regular trainer
 * doesn't trip over it.
 */
import { useState } from "react";
import { getIapDiagnostics, type IapDiagnostics } from "@/lib/iap";

export function PaywallDiagnosticsPanel() {
  const [diag, setDiag] = useState<IapDiagnostics | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function read() {
    setBusy(true);
    try {
      setDiag(await getIapDiagnostics());
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!diag) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable on web preview; tester can long-press
      // the rendered <pre> instead.
    }
  }

  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
      data-testid="settings-paywall-diagnostics"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
        Paywall / IAP diagnostics
      </p>
      <p
        className="text-[11px] leading-relaxed mb-1"
        style={{ color: diag?.iapDisabled ? "hsl(43 96% 62%)" : "hsl(var(--muted-foreground))" }}
        data-testid="paywall-diag-summary"
      >
        {diag ? diag.summary : "Not read yet"}
      </p>
      {diag ? (
        <p className="text-[11px] text-muted-foreground leading-relaxed mb-3" data-testid="paywall-diag-key">
          Key loaded: {diag.keyLoaded ? "yes" : "no"}
          {diag.keyEnvVar ? ` (${diag.keyEnvVar})` : ""} · plugin{" "}
          {diag.pluginAvailable ? "yes" : "no"} · configured {diag.sdkConfigured ? "yes" : "no"} ·{" "}
          {diag.packageCount} pkg
        </p>
      ) : (
        <div className="mb-2" />
      )}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={read}
          disabled={busy}
          data-testid="button-paywall-diag-read"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? "Reading…" : "Read"}
        </button>
        <button
          type="button"
          onClick={copy}
          disabled={!diag || busy}
          data-testid="button-paywall-diag-copy"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
            opacity: !diag || busy ? 0.4 : 1,
          }}
        >
          {copied ? "Copied" : "Copy JSON"}
        </button>
      </div>
      {diag && (
        <pre
          className="mt-3 rounded-xl p-3 text-[10px] leading-snug overflow-x-auto diag-code"
          style={{ maxHeight: 320 }}
          data-testid="paywall-diag-json"
        >
          {JSON.stringify(diag, null, 2)}
        </pre>
      )}
    </div>
  );
}
